import * as React from "react";
import { connect } from "react-redux";

import ClipboardJS from "clipboard";
import * as querystring from "querystring";
import * as toastr from "toastr";

import { Button } from "../Common/Button/Button";
import { FancyDropdown, FancyDropdownAlignment, FancyDropdownMode } from "../Common/FancyDropdown/FancyDropdown";
import { YearAndMonthPicker } from "./YearAndMonthPicker";

import { IOption } from "../../classes/option";
import { IText } from "../../classes/text";
import { IFancyDropdownOptionProps } from "../common/FancyDropdown/FancyDropdownOption";

import * as dates from "../../reducers/changeTimePeriod";
import * as viewModes from "../../reducers/viewModes";
import * as buttonEvents from "../Common/buttonEvents";

export interface IMainToolbarProps {
  calendarId: number;
  calendarName: string;
  currentDate: Date;
  hasCreatePermission: boolean;
  hasServerData: number;
  localFromDate: Date;
  localToDate: Date;
  numAjaxCallsInProgress: number;
  portalId: number;
  pageId: number;
  pageName: string;
  objectId: number;
  contextId: number;
  parentId: number;
  portletInstanceId: number;
  selectedDate: Date;
  todaysDate: Date;
  useModernAddAndEditEventScreen: boolean;
  viewMode: string;
  viewModeOptions: IOption[];
  text: IText;
  onRequestServersForCalendar: (portletInstanceId: number, calendarId: number) => any;
  onSetCurrentYear: (event: any) => any;
  onSetCurrentMonth: (event: any) => any;
  onSetCurrentDateToToday: (event: any) => any;
  onSetEventScreenMode: (eventScreenMode: string) => any;
  onSetViewMode: (event: any) => any;
  onSetCurrentDateToPreviousPeriod: (event: any) => any;
  onSetCurrentDateToNextPeriod: (event: any) => any;
  onOpenSelectEventsFromOtherCalendarsModal: (event: any) => any;
  onClearEvent: () => void;
}

export interface IMainToolbarState {
  tabIndexedItemID: string;
  hideAddEventsButton: boolean;
  copyLinkAdded: boolean;
}

export class MainToolbar extends React.Component<IMainToolbarProps, IMainToolbarState> {

  public constructor(props: any, context: any) {
    super(props, context);

    this.state = { tabIndexedItemID: "prevbutton_" + props.portletInstanceId, hideAddEventsButton: false, copyLinkAdded: false }; // Only one item in the Search Toolbar has a tabIndex at a time, and it is retained when  user tabs into or out of the Search Toolbar.

    this.onFocussed = this.onFocussed.bind(this);
    this.onOpenSelectEventsModal = this.onOpenSelectEventsModal.bind(this);
  }

  public onFocussed = (itemID: string): void => {
    this.setState({ tabIndexedItemID: itemID });
  }

  public onOpenSelectEventsModal = (event: any): any => {
    if (!this.props.hasServerData) {
      this.props.onRequestServersForCalendar(this.props.portletInstanceId, this.props.calendarId); // VERIFY THIS!!!! Is same as calendarId?
    }
    this.props.onOpenSelectEventsFromOtherCalendarsModal(event);
  }

  public onClickAddEventButton = (event: any): void => {
    event.preventDefault();
    if (this.props.useModernAddAndEditEventScreen) {
      buttonEvents.onAddEvent_DisplayModernAddEventScreen(event, this.props.onClearEvent, this.props.onSetEventScreenMode);
    } else {
      buttonEvents.onAddEvent_DisplayClassicAddEventScreen(event);
    }
  }

  public componentDidMount() {
    this.setWhetherToHideEventsButton();
    this.AddCopyLinkToThisViewToButton();
  }

  public render() {

    const props = this.props;

    buttonEvents.importProps(props);

    let nextMessage = "";
    let previousMessage = "";

    const addEventsOptions: IFancyDropdownOptionProps[] = [
      { text: props.text.mainToolbar.newEvent, value: "1", isSelected: false, fontAwesomeIcon: "fa-calendar-plus-o", onSelected: this.onClickAddEventButton },
      { text: props.text.mainToolbar.importEvents, value: "2", isSelected: false, fontAwesomeIcon: "fa-download", onSelected: buttonEvents.onImportEvents },
    ];

    const URLForCopyLinkToThisView = buttonEvents.GetURLForCopyLink();
    const otherOptions: IFancyDropdownOptionProps[] = [
      { text: props.text.mainToolbar.icalRSSFeed, value: "1", isSelected: false, fontAwesomeIcon: "fa-calendar", onSelected: buttonEvents.onICALRSSFeed },
      { text: props.text.mainToolbar.subscribeEmail, value: "2", isSelected: false, fontAwesomeIcon: "fa-envelope", onSelected: buttonEvents.onSubscribeToEmailAlerts },
      { text: props.text.mainToolbar.exportEvents, value: "3", isSelected: false, fontAwesomeIcon: "fa-upload", onSelected: buttonEvents.onExportEvents },
      { text: props.text.mainToolbar.copyLink, value: "4", isSelected: false, fontAwesomeIcon: "fa-copy", extraListItemCSSClass: "copyLink", clipboardText: URLForCopyLinkToThisView },
      { text: props.text.mainToolbar.viewOtherCalendars, value: "5", isSelected: false, fontAwesomeIcon: "fa-calendar", onSelected: this.onOpenSelectEventsModal },
      // { text: props.text.mainToolbar.print, value: "5", isSelected: false, fontAwesomeIcon: "fa-print", onSelected: buttonEvents.onPrint },
    ];

    const viewModeOptions: IFancyDropdownOptionProps[] = [];
    for (const option of props.viewModeOptions) {
      viewModeOptions.push({ text: option.text, value: option.value, onSelected: props.onSetViewMode });
    }

    const throwError = (event: any) => {
      event.preventDefault();
      throw new Error("Error thrown!");
    };

    const setNextAndPreviousMessage = () => {
      switch (props.viewMode.toUpperCase()) {
        case ("DAILY"):
        case ("DAILYLIST"): {
          nextMessage = props.text.mainToolbar.nextDay;
          previousMessage = props.text.mainToolbar.previousDay;
          break;
        }
        case ("WEEKLY"): {
          nextMessage = props.text.mainToolbar.nextWeek;
          previousMessage = props.text.mainToolbar.previousWeek;
          break;
        }
        case ("MONTHLY"): {
          nextMessage = props.text.mainToolbar.nextMonth;
          previousMessage = props.text.mainToolbar.previousMonth;
          break;
        }
        case ("MONTHLYLIST"): {
          nextMessage = props.text.mainToolbar.nextMonth;
          previousMessage = props.text.mainToolbar.previousMonth;
          break;
        }
        default: {
          nextMessage = "???";
          previousMessage = "???";
        }
      }
    };

    setNextAndPreviousMessage();

    const isLoading = props.numAjaxCallsInProgress > 0;

    const prevbuttonID = "prevbutton_" + props.portletInstanceId;
    const nextbuttonID = "nextbutton_" + props.portletInstanceId;
    const todaybuttonID = "todaybutton_" + props.portletInstanceId;
    const eventviewdropdownID = "eventviewdropdown_" + props.portletInstanceId;
    const addeventsdropdownID = "addeventsdropdown_" + props.portletInstanceId;
    const otherdropdownID = "otherdropdown" + props.portletInstanceId;

    return (
      <React.Fragment>
        <div className="CAL-topControls">

          <YearAndMonthPicker
            currentDate={props.currentDate}
            monthNamesComplete={props.text.monthNamesComplete}
            monthNamesAbbreviated={props.text.monthNamesAbbreviated}
            numAjaxCallsInProgress={props.numAjaxCallsInProgress}
            text={props.text}
            onSetCurrentYear={props.onSetCurrentYear}
            onSetCurrentMonth={props.onSetCurrentMonth}
          />

          <ul className="CAL-optionButtons js-nav-system">
            <li className="js-nav-system__item" role="button">

              <Button
                itemID={prevbuttonID}
                cssID="prevButton"
                text={previousMessage}
                fontawesomeIconCSS="fa-arrow-left"
                onClick={props.onSetCurrentDateToPreviousPeriod}
                disabled={isLoading}
                tabIndex={this.setTabIndex(prevbuttonID)}
                onFocussed={this.onFocussed}
                tooltipTextOnFocus={props.text.mainToolbar.useArrowKeysToNavigateThroughOptions}
              />
              
             </li>
            <li className="js-nav-system__item" role="button">

              <Button
                itemID={nextbuttonID}
                cssID="nextButton"
                text={nextMessage}
                fontawesomeIconCSS="fa-arrow-right"
                onClick={props.onSetCurrentDateToNextPeriod}
                disabled={isLoading}
                tabIndex={this.setTabIndex(nextbuttonID)}
                onFocussed={this.onFocussed}
              />

            </li>
            <li className="CLA-dropdownWrapper js-nav-system__item" role="menu">

              <FancyDropdown
                itemID={eventviewdropdownID}
                defaultText={"Monthly"}
                mode={FancyDropdownMode.textButton}
                options={viewModeOptions}
                selectedValue={props.viewMode.toString()}
                tabIndex={this.setTabIndex(eventviewdropdownID)}
                onFocussed={this.onFocussed}
              />

            </li>
            <li className="js-nav-system__item" role="button">

              <Button
                itemID={todaybuttonID}
                cssID=""
                text={props.text.mainToolbar.today}
                visibleText={true}
                fontawesomeIconCSS="fa-calendar"
                onClick={props.onSetCurrentDateToToday}
                disabled={isLoading}
                tabIndex={this.setTabIndex(todaybuttonID)}
                onFocussed={this.onFocussed}
              />

            </li>
            {props.hasCreatePermission && !this.state.hideAddEventsButton &&
              <li className="CLA-dropdownWrapper js-nav-system__item" role="menu">

                <FancyDropdown
                  itemID={addeventsdropdownID}
                  alignment={FancyDropdownAlignment.right}
                  defaultText="Add Events Menu"
                  fontAwesomeIcon="fa-plus"
                  mode={FancyDropdownMode.iconButton}
                  options={addEventsOptions}
                  tabIndex={this.setTabIndex(addeventsdropdownID)}
                  onFocussed={this.onFocussed}
                />

              </li>
            }
            <li className="CLA-dropdownWrapper js-nav-system__item" role="menu">

              <FancyDropdown
                itemID={otherdropdownID}
                alignment={FancyDropdownAlignment.right}
                defaultText="Other Options Menu"
                fontAwesomeIcon="fa-share-alt"
                mode={FancyDropdownMode.iconButton}
                options={otherOptions}
                tabIndex={this.setTabIndex(otherdropdownID)}
                onFocussed={this.onFocussed}
                optionalClassName={"wideDropdown"}
              />

            </li>

          </ul>
        </div>
      </React.Fragment >
    );

  };

  private setTabIndex = (itemID: string): number => {
    return itemID === this.state.tabIndexedItemID ? 0 : -1;
  }

  private setWhetherToHideEventsButton = () => {
    // This is used when previewing the calendar, which should appear in Guest Mode, without the Add Events button.
    // Activated by adding pageaction=preview to the querystring.

    let theLocation = location.search;
    if (theLocation && theLocation.length > 0 && theLocation[0] === "?") { theLocation = theLocation.substring(1, theLocation.length); }

    const querystringObj = querystring.parse(theLocation);
    if (querystringObj && querystringObj.pageaction) {
      if (querystringObj.pageaction === "preview") {
        this.setState({ hideAddEventsButton: true });
      }
    }
  }

  private AddCopyLinkToThisViewToButton() {
    if (!this.state.copyLinkAdded) {

      this.setState({ copyLinkAdded: true });

      const copyLinkAnchorSelector = "#portlet" + this.props.portletInstanceId.toString() + " li.copyLink";
      const clipboard = new ClipboardJS(copyLinkAnchorSelector);

      clipboard.on("success", (e) => {
        window.alert(this.props.text.mainToolbar.theLinkIsNowCopied);
        e.clearSelection();
      });

      clipboard.on("error", (e) => {
        console.error("Action:", e.action);
        console.error("Trigger:", e.trigger);
      });
    }

  }

};
